/**
 * WebSocket Provider Component
 * Provides WebSocket context for real-time collaboration
 */

import React, { createContext, useContext, useEffect, useState, ReactNode, useCallback, useMemo } from 'react';
import { webSocketService, UserPresence, CollaborationEvent } from '@/services/websocket';
import { useAuthStore } from '@/stores/auth';
import { useWorkflowStore } from '@/stores/workflow';

interface WebSocketContextType {
  connectionState: string;
  activeUsers: UserPresence[];
  collaborationEvents: CollaborationEvent[];
  updatePresence: (page: string, isActive?: boolean) => void;
  sendTypingIndicator: (workflowId: string, isTyping: boolean) => void;
  broadcastWorkflowUpdate: (workflowId: string, changes: any) => void;
  subscribeToMessage: (type: string, handler: (data: any) => void) => () => void;
  subscribeToCollaboration: (handler: (event: CollaborationEvent) => void) => () => void;
  joinProject: (projectId: string) => void;
  leaveProject: (projectId: string) => void;
}

const WebSocketContext = createContext<WebSocketContextType | null>(null);

interface WebSocketProviderProps {
  children: ReactNode;
}

export const WebSocketProvider: React.FC<WebSocketProviderProps> = ({ children }) => {
  const [connectionState, setConnectionState] = useState<string>('disconnected');
  const [activeUsers, setActiveUsers] = useState<UserPresence[]>([]);
  const [collaborationEvents, setCollaborationEvents] = useState<CollaborationEvent[]>([]);
  const [subscribedTypes] = useState<Set<string>>(new Set()); // Track subscriptions (DEFECT-083)
  const { user } = useAuthStore();

  useEffect(() => {
    // Update connection state periodically
    const interval = setInterval(() => {
      setConnectionState(webSocketService.getConnectionState());
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    // Subscribe to presence updates
    const unsubscribePresence = webSocketService.subscribeToPresence((presence) => {
      setActiveUsers(presence);
    });

    // Subscribe to collaboration events
    const unsubscribeCollaboration = webSocketService.subscribeToCollaboration((event) => {
      setCollaborationEvents(prev => [...prev.slice(-49), event]); // Keep last 50 events
    });

    // Subscribe to system_alert early (before AuthGuard) so startup
    // alerts replayed on connection are captured into the Zustand store.
    const unsubscribeSystemAlert = webSocketService.subscribeToMessage('system_alert', (event: any) => {
      if (event?.alert) {
        useWorkflowStore.getState().addSystemAlert({
          level: event.alert.level || 'INFO',
          message: event.alert.message || 'System alert',
          component: event.alert.component || 'system',
          timestamp: event.timestamp || new Date().toISOString(),
        });
      }
    });

    const unsubscribeSystemAlertsCleared = webSocketService.subscribeToMessage('system_alerts_cleared', (event: any) => {
      const component = event?.component;
      if (typeof component === 'string' && component.length > 0) {
        useWorkflowStore.getState().removeSystemAlertsByComponent(component);
      }
    });

    return () => {
      unsubscribePresence();
      unsubscribeCollaboration();
      unsubscribeSystemAlert();
      unsubscribeSystemAlertsCleared();
    };
  }, []);

  useEffect(() => {
    // Update user presence when user changes and connection is ready
    if (user && connectionState === 'connected') {
      webSocketService.updateUserPresence(window.location.pathname, true);
    }
  }, [user, connectionState]);

  const updatePresence = (page: string, isActive: boolean = true) => {
    webSocketService.updateUserPresence(page, isActive);
  };

  const sendTypingIndicator = (workflowId: string, isTyping: boolean) => {
    webSocketService.sendTypingIndicator(workflowId, isTyping);
  };

  // ✅ FIX DEFECT-002: Memoize functions to prevent reconnection cycles
  const broadcastWorkflowUpdate = useCallback((workflowId: string, changes: any) => {
    webSocketService.broadcastWorkflowUpdate(workflowId, changes);
  }, []);

  const subscribeToMessage = useCallback((type: string, handler: (data: any) => void) => {
    subscribedTypes.add(type);
    return webSocketService.subscribeToMessage(type, handler);
  }, [subscribedTypes]);

  const subscribeToCollaboration = useCallback((handler: (event: CollaborationEvent) => void) => {
    return webSocketService.subscribeToCollaboration(handler);
  }, []);
  
  const joinProject = useCallback((projectId: string) => {
    webSocketService.joinProject(projectId);
  }, []);
  
  const leaveProject = useCallback((projectId: string) => {
    webSocketService.leaveProject(projectId);
  }, []);

  // ✅ FIX DEFECT-002: Memoize context value to prevent provider re-renders
  const contextValue = useMemo<WebSocketContextType>(() => ({
    connectionState,
    activeUsers,
    collaborationEvents,
    updatePresence,
    sendTypingIndicator,
    broadcastWorkflowUpdate,
    subscribeToMessage,
    subscribeToCollaboration,
    joinProject,
    leaveProject,
  }), [connectionState, activeUsers, collaborationEvents, updatePresence, sendTypingIndicator, broadcastWorkflowUpdate, subscribeToMessage, subscribeToCollaboration, joinProject, leaveProject]);

  return (
    <WebSocketContext.Provider value={contextValue}>
      {children}
    </WebSocketContext.Provider>
  );
};

export const useWebSocket = (): WebSocketContextType => {
  const context = useContext(WebSocketContext);
  if (!context) {
    throw new Error('useWebSocket must be used within a WebSocketProvider');
  }
  return context;
};

export default WebSocketProvider;